import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2 } from '@angular/core';

@Directive({
	selector: '[pepsTooltip],[pepsTooltipHtml]',
	standalone: true,
})
export class TooltipDirective implements OnDestroy {
	@Input('pepsTooltip') text = '';
	@Input('pepsTooltipHtml') html = '';
	@Input('pepsTooltipSize') fontSize = '10px';

	private el: HTMLElement | null = null;

	constructor(private host: ElementRef<HTMLElement>, private renderer: Renderer2) {}

	@HostListener('mouseenter')
	show(): void {
		if (!this.text && !this.html) return;

		const isHtml = !!this.html;
		// Mobile/tablette : jamais de tooltip plus large que l'écran (desktop inchangé).
		const isSmallScreen = window.innerWidth < 1024;

		this.el = this.renderer.createElement('div') as HTMLElement;
		if (isHtml) {
			this.el.innerHTML = this.html;
		} else {
			this.el.textContent = this.text;
		}

		const styles: Record<string, string> = {
			position: 'fixed',
			zIndex: '9999',
			pointerEvents: 'none',
			padding: isHtml ? '10px 14px' : '6px 10px',
			background: 'var(--color-peps-dark-800, #111)',
			border: '1px solid var(--color-peps-dark-500, #333)',
			color: 'rgba(255,255,255,0.6)',
			fontSize: this.fontSize,
			fontFamily: 'NotoSans',
			letterSpacing: isHtml ? 'normal' : '0.2em',
			textTransform: isHtml ? 'none' : 'uppercase',
			whiteSpace: isSmallScreen ? 'normal' : (isHtml ? 'normal' : 'nowrap'),
			maxWidth: isSmallScreen ? 'calc(100vw - 24px)' : 'none',
			boxSizing: 'border-box',
			opacity: '0',
			transition: 'opacity 0.15s ease',
		};
		Object.entries(styles).forEach(([k, v]) => this.renderer.setStyle(this.el, k, v));

		document.body.appendChild(this.el);

		// Positionne après un micro-tick pour avoir les dimensions réelles
		requestAnimationFrame(() => {
			if (!this.el) return;

			const anchor = this.host.nativeElement.getBoundingClientRect();
			const tip = this.el.getBoundingClientRect();
			const gap = 6;
			const vw = window.innerWidth;
			const vh = window.innerHeight;

			// Préférence : sous l'élément, centré horizontalement
			let top = anchor.bottom + gap;
			let left = anchor.left + anchor.width / 2 - tip.width / 2;

			// Déborde en bas → passe au-dessus
			if (top + tip.height > vh - 8) {
				top = anchor.top - tip.height - gap;
			}

			// Clamp horizontal
			left = Math.max(8, Math.min(left, vw - tip.width - 8));

			this.renderer.setStyle(this.el, 'top', `${top}px`);
			this.renderer.setStyle(this.el, 'left', `${left}px`);
			this.renderer.setStyle(this.el, 'opacity', '1');
		});
	}

	@HostListener('mouseleave')
	hide(): void {
		this.remove();
	}

	ngOnDestroy(): void {
		this.remove();
	}

	private remove(): void {
		if (this.el) {
			this.el.remove();
			this.el = null;
		}
	}
}
